// ============================================
// Chat Message Pagination (ES6)
// 위로 스크롤 시 이전 메시지 로드
// ============================================

import { getCurrentUser, getCurrentRoomId, getCurrentSessionId, getCurrentChatData } from '../../app/state.js';
import { formatAIMessage } from '../parser.js';
import { formatUserMessage } from '../send.js';
import { setupMessageLongPress } from '../context-menu.js';
import { showImageModal } from '../../image.js';
import { createLazyImage } from '../../shared/lazy-image.js';
import { showWorldInfo, showCharacterProfile } from '../load.js';

// ============================================
// 상수 및 상태
// ============================================
const LOAD_MORE_LIMIT = 30;

let isLoadingMore = false;
let hasMoreMessages = true;
let oldestMessageTimestamp = null;
let loadMoreObserver = null;

// ============================================
// 페이지네이션 상태 관리
// ============================================

/**
 * 페이지네이션 상태 초기화
 * @param {boolean} hasMore - 더 로드할 메시지가 있는지
 * @param {Timestamp} oldestTimestamp - 현재 로드된 가장 오래된 메시지의 timestamp
 */
export function resetPagination(hasMore = true, oldestTimestamp = null) {
  isLoadingMore = false;
  hasMoreMessages = hasMore;
  oldestMessageTimestamp = oldestTimestamp;
}

/**
 * 페이지네이션 상태 조회
 */
export function getPaginationState() {
  return {
    isLoadingMore,
    hasMoreMessages,
    oldestMessageTimestamp
  };
}

// ============================================
// 무한 스크롤 설정
// ============================================

/**
 * 위로 스크롤 감지 설정 (IntersectionObserver)
 * @param {HTMLElement} container - 메시지 컨테이너
 */
export function setupScrollToLoadMore(container) {
  if (!container) return;

  // 기존 observer 정리
  if (loadMoreObserver) {
    loadMoreObserver.disconnect();
  }

  // 로드 트리거 엘리먼트 생성/찾기
  let loadTrigger = container.querySelector('.load-more-trigger');
  if (!loadTrigger) {
    loadTrigger = document.createElement('div');
    loadTrigger.className = 'load-more-trigger';
    // 캐릭터 프로필/월드 정보 다음에 삽입 (있으면)
    const profileCard = container.querySelector('.character-profile-card, .world-info');
    if (profileCard && profileCard.nextSibling) {
      container.insertBefore(loadTrigger, profileCard.nextSibling);
    } else if (profileCard) {
      profileCard.after(loadTrigger);
    } else {
      container.insertBefore(loadTrigger, container.firstChild);
    }
  }

  loadMoreObserver = new IntersectionObserver((entries) => {
    if (entries[0].isIntersecting && hasMoreMessages && !isLoadingMore) {
      loadOlderMessages(container);
    }
  }, {
    root: container,
    rootMargin: '200px 0px 0px 0px',  // 상단 200px 전에 트리거
    threshold: 0
  });

  loadMoreObserver.observe(loadTrigger);
}

/**
 * 페이지네이션 정리
 */
export function cleanupPagination() {
  if (loadMoreObserver) {
    loadMoreObserver.disconnect();
    loadMoreObserver = null;
  }
  resetPagination();
}

// ============================================
// 이전 메시지 로드
// ============================================

/**
 * 이전 메시지 로드 (위로 스크롤 시 호출)
 * @param {HTMLElement} container - 메시지 컨테이너
 */
async function loadOlderMessages(container) {
  if (isLoadingMore || !hasMoreMessages) return;

  const roomId = getCurrentRoomId();
  const sessionId = getCurrentSessionId();
  const userId = getCurrentUser()?.uid;

  if (!roomId || !sessionId || !userId || !oldestMessageTimestamp) return;

  isLoadingMore = true;

  // 로딩 스피너 표시
  const loadingEl = document.createElement('div');
  loadingEl.className = 'load-more-spinner';
  const spinner = document.createElement('div');
  spinner.className = 'spinner';
  loadingEl.appendChild(spinner);

  const trigger = container.querySelector('.load-more-trigger');
  if (trigger) {
    trigger.after(loadingEl);
  } else {
    container.insertBefore(loadingEl, container.firstChild);
  }

  // 현재 스크롤 높이 저장 (로드 후 위치 유지용)
  const prevScrollHeight = container.scrollHeight;

  try {
    const db = firebase.firestore();
    const sessionRef = db.collection('users').doc(userId)
      .collection('chatRooms').doc(roomId)
      .collection('sessions').doc(sessionId);

    // 가장 오래된 메시지보다 더 오래된 메시지 로드
    const query = sessionRef.collection('messages')
      .orderBy('createdAt', 'desc')
      .startAfter(oldestMessageTimestamp)
      .limit(LOAD_MORE_LIMIT);

    const snapshot = await query.get();

    // 로딩 스피너 제거
    loadingEl.remove();

    if (snapshot.empty) {
      hasMoreMessages = false;
      // 더 이상 메시지가 없으면 트리거 제거
      trigger?.remove();
      // 첫 메시지까지 로드 완료 - 프로필 표시
      showCharacterProfileIfNeeded(container);
      return;
    }

    // 더 로드할 메시지가 있는지
    hasMoreMessages = snapshot.docs.length >= LOAD_MORE_LIMIT;

    // 메시지를 시간순으로 정렬 (오래된 것 먼저)
    const newMessages = snapshot.docs
      .map(doc => ({ id: doc.id, ...doc.data() }))
      .reverse();

    // 가장 오래된 timestamp 업데이트
    if (snapshot.docs.length > 0) {
      oldestMessageTimestamp = snapshot.docs[snapshot.docs.length - 1].data().createdAt;
    }

    // 메시지 삽입 (트리거 다음에)
    const insertPoint = trigger?.nextSibling || container.firstChild;
    newMessages.forEach(msg => {
      const timestamp = msg.createdAt?.toDate ? msg.createdAt.toDate() : null;
      const messageEl = createMessageElement(msg.role, msg.content, timestamp, msg.id, msg.turn);
      container.insertBefore(messageEl, insertPoint);
    });

    // 스크롤 위치 유지 (새 콘텐츠 높이만큼 보정)
    const newScrollHeight = container.scrollHeight;
    container.scrollTop = newScrollHeight - prevScrollHeight;

    // 더 이상 메시지가 없으면 트리거 제거 및 프로필 표시
    if (!hasMoreMessages) {
      trigger?.remove();
      // 첫 메시지까지 로드 완료 - 프로필 표시
      showCharacterProfileIfNeeded(container);
    }

  } catch (e) {
    console.error('[Pagination] 이전 메시지 로드 실패:', e);
    loadingEl.remove();
  } finally {
    isLoadingMore = false;
  }
}

// ============================================
// 메시지 엘리먼트 생성 (renderer.js와 동일 로직)
// ============================================

/**
 * 메시지 DOM 엘리먼트 생성
 * 주의: formattedContent는 formatAIMessage/formatUserMessage에서 이미 XSS 방지 처리됨
 */
function createMessageElement(role, content, timestamp, messageId, turn) {
  // 메모리 마커 제거
  if (content && content.includes('<<<')) {
    content = content.split('<<<')[0].trim();
  }

  const div = document.createElement('div');
  const displayRole = (role === 'model') ? 'assistant' : role;
  div.className = `message ${displayRole}`;

  if (messageId) {
    div.dataset.messageId = messageId;
  }
  if (timestamp) {
    div.dataset.timestamp = timestamp.getTime();
  }
  if (turn) {
    div.dataset.turn = turn;
  }

  let formattedContent;
  if (role === 'assistant' || role === 'model') {
    formattedContent = formatAIMessage(content);
    if (content && content.includes('{U}')) {
      div.classList.add('has-user-quote');
    }
    setupMessageLongPress(div, content);
  } else {
    formattedContent = formatUserMessage(content);
    setupMessageLongPress(div, content);
  }

  // AI 메시지에 액션 버튼 추가
  const isAssistant = role === 'assistant' || role === 'model';

  // message-content div 생성 (formattedContent는 이미 sanitize됨)
  const contentDiv = document.createElement('div');
  contentDiv.className = 'message-content';
  // formatAIMessage와 formatUserMessage는 내부에서 escapeHtml 처리
  contentDiv.innerHTML = formattedContent;
  div.appendChild(contentDiv);

  // rawContent 저장 (AI 메시지만)
  if (isAssistant) {
    div.dataset.rawContent = content;
  }

  return div;
}

/**
 * 메시지 액션 버튼 생성 (DOM API 사용)
 */
function createMessageActions() {
  const actionsDiv = document.createElement('div');
  actionsDiv.className = 'message-actions';

  // 이미지 생성 버튼
  const imgBtn = document.createElement('button');
  imgBtn.className = 'message-action-btn';
  imgBtn.dataset.action = 'generateSceneImage';

  const imgSvg = createSvgElement('0 0 24 24');
  const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
  rect.setAttribute('x', '3');
  rect.setAttribute('y', '3');
  rect.setAttribute('width', '18');
  rect.setAttribute('height', '18');
  rect.setAttribute('rx', '2');
  rect.setAttribute('ry', '2');
  imgSvg.appendChild(rect);
  const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
  circle.setAttribute('cx', '8.5');
  circle.setAttribute('cy', '8.5');
  circle.setAttribute('r', '1.5');
  imgSvg.appendChild(circle);
  const polyline = document.createElementNS('http://www.w3.org/2000/svg', 'polyline');
  polyline.setAttribute('points', '21 15 16 10 5 21');
  imgSvg.appendChild(polyline);
  imgSvg.style.cssText = 'width:14px;height:14px;margin-right:4px;';
  imgBtn.appendChild(imgSvg);
  imgBtn.appendChild(document.createTextNode('이미지'));
  actionsDiv.appendChild(imgBtn);

  // 재생성 버튼
  const regenBtn = document.createElement('button');
  regenBtn.className = 'message-action-btn icon-only';
  regenBtn.dataset.action = 'regenerate';
  regenBtn.title = '재생성';

  const regenSvg = createSvgElement('0 0 24 24');
  const path1 = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  path1.setAttribute('d', 'M1 4v6h6M23 20v-6h-6');
  regenSvg.appendChild(path1);
  const path2 = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  path2.setAttribute('d', 'M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4-4.64 4.36A9 9 0 0 1 3.51 15');
  regenSvg.appendChild(path2);
  regenBtn.appendChild(regenSvg);
  actionsDiv.appendChild(regenBtn);

  // 편집 버튼
  const editBtn = document.createElement('button');
  editBtn.className = 'message-action-btn icon-only';
  editBtn.dataset.action = 'editMessage';
  editBtn.title = '편집';

  const editSvg = createSvgElement('0 0 24 24');
  const editPath1 = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  editPath1.setAttribute('d', 'M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7');
  editSvg.appendChild(editPath1);
  const editPath2 = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  editPath2.setAttribute('d', 'M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z');
  editSvg.appendChild(editPath2);
  editBtn.appendChild(editSvg);
  actionsDiv.appendChild(editBtn);

  return actionsDiv;
}

/**
 * SVG 엘리먼트 생성 헬퍼
 */
function createSvgElement(viewBox) {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', viewBox);
  svg.setAttribute('fill', 'none');
  svg.setAttribute('stroke', 'currentColor');
  svg.setAttribute('stroke-width', '2');
  return svg;
}

/**
 * Scene Image 엘리먼트 생성
 */
function createSceneImageElement(imageUrl, timestamp) {
  const div = document.createElement('div');
  div.className = 'message assistant scene-image';

  if (timestamp) {
    div.dataset.timestamp = timestamp.getTime();
  }

  const imageWrapper = createLazyImage(imageUrl, '생성된 이미지', 'scene-image-skeleton', {
    onClick: () => showImageModal(imageUrl, '생성된 이미지')
  });

  const sceneContainer = document.createElement('div');
  sceneContainer.className = 'scene-image-container';
  sceneContainer.dataset.url = imageUrl;
  sceneContainer.appendChild(imageWrapper);

  // 확대 아이콘 오버레이
  const overlay = document.createElement('div');
  overlay.className = 'scene-image-overlay';
  const svg = createSvgElement('0 0 24 24');
  const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  path.setAttribute('d', 'M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7');
  svg.appendChild(path);
  overlay.appendChild(svg);
  sceneContainer.appendChild(overlay);

  div.appendChild(sceneContainer);
  return div;
}

// ============================================
// 프로필 표시 (모든 메시지 로드 완료 시)
// ============================================

/**
 * 첫 메시지까지 로드 완료 시 캐릭터 프로필 표시
 * @param {HTMLElement} container - 메시지 컨테이너
 */
function showCharacterProfileIfNeeded(container) {
  const chatData = getCurrentChatData();
  if (!container || !chatData) return;

  // 이미 프로필이 있으면 스킵
  if (container.querySelector('.character-profile-card, .world-info')) return;

  // 현재 스크롤 높이 저장
  const prevScrollHeight = container.scrollHeight;

  if (chatData.mode === 'story' && chatData.world) {
    showWorldInfo(chatData.world, chatData.characters, chatData.scenario);
  } else if (chatData.character) {
    showCharacterProfile(chatData.character, chatData.scenario);
  }

  // 스크롤 위치 유지 (프로필 추가로 인한 shift 보정)
  requestAnimationFrame(() => {
    const addedHeight = container.scrollHeight - prevScrollHeight;
    if (addedHeight > 0) {
      container.scrollTop += addedHeight;
    }
  });
}
